import { openDB } from 'idb'
import { APP_DATABASE_NAME } from '../utils/storageNames'
import { workspaceControlKey, workspaceDatabaseName, type WorkspaceScope } from './workspace'
import type { ContextsAccountWorkspace } from '../contexts/account/contract'

/** Display metadata only; the immutable owner ID remains the account boundary. */
export const WORKSPACE_ACCOUNT_IDENTITY_KEY = 'workspace-account-identity:v1'
export interface WorkspaceAccountIdentity {
  owner: string
  name: string | null
  email: string | null
}
export interface RetainedAccountCopy extends ContextsAccountWorkspace {
  identity?: WorkspaceAccountIdentity
}
function readAccountIdentity(value: unknown, owner: string): WorkspaceAccountIdentity | undefined {
  if (typeof value !== 'string') return undefined
  try { value = JSON.parse(value) } catch { return undefined }
  if (!value || typeof value !== 'object' || !('owner' in value) || value.owner !== owner) return undefined
  return {
    owner,
    name: 'name' in value && typeof value.name === 'string' ? value.name.trim() || null : null,
    email: 'email' in value && typeof value.email === 'string' ? value.email.trim() || null : null,
  }
}

/** Read ownership metadata only. Never read another account's notes to render a warning. */
export async function findRetainedAccountCopies(scope: WorkspaceScope, currentOwner: string | null): Promise<RetainedAccountCopy[]> {
  const prefix = workspaceDatabaseName(scope, null).replace(/guest$/, 'account:')
  const names = (await indexedDB.databases()).map(db => db.name).filter((name): name is string => Boolean(name && (name === APP_DATABASE_NAME || name.startsWith(prefix))))
  const result: RetainedAccountCopy[] = []
  for (const databaseName of names) {
    let missing = false
    const db = await openDB(databaseName, undefined, { upgrade(_db, version, _next, tx) {
      if (version === 0) { missing = true; void tx.done.catch(() => undefined); tx.abort() }
    } }).catch(error => { if (!missing) throw error; return null })
    if (!db) continue
    try {
      if (!db.objectStoreNames.contains('meta')) continue
      const owner = (await db.get('meta','sync-owner'))?.value
      const environment = (await db.get('meta','sync-environment'))?.value
      const project = (await db.get('meta','workspace-project:v1'))?.value
      if (typeof owner !== 'string' || !owner || owner === currentOwner || environment !== scope.environment || (project && project !== scope.project)) continue
      if (databaseName !== APP_DATABASE_NAME && databaseName !== workspaceDatabaseName(scope,owner)) throw new Error('Offline copy ownership does not match its storage namespace.')
      const identity = readAccountIdentity((await db.get('meta', WORKSPACE_ACCOUNT_IDENTITY_KEY))?.value, owner)
      result.push({databaseName, owner, scope, ...(identity ? { identity } : {})})
    } finally { db.close() }
  }
  return result.sort((a,b) => a.databaseName.localeCompare(b.databaseName))
}
const decisionKey = (scope: WorkspaceScope, owner: string | null) => `${workspaceControlKey(scope)}:entry-choice:${owner ?? 'guest'}`
const signature = (copies: ContextsAccountWorkspace[]) => JSON.stringify(copies.map(({databaseName,owner}) => [databaseName,owner]).sort())
export function hasAccountEntryDecision(scope: WorkspaceScope, owner: string | null, copies: ContextsAccountWorkspace[]): boolean {
  return copies.length === 0 || localStorage.getItem(decisionKey(scope,owner)) === signature(copies)
}
export function rememberAccountEntryDecision(scope: WorkspaceScope, owner: string | null, copies: ContextsAccountWorkspace[]): void {
  localStorage.setItem(decisionKey(scope,owner),signature(copies))
}
